import * as React from "react";
import { Editor } from "@tiptap/react";

import { Divider } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { colors } from "theme/colors";

import { RowActionButton, ActionMenu, MenuItem, MenuBackdrop } from "../table-wrapper/styles";

export const TableRowActions: React.FC<{
  editor: Editor;
  tableElement: HTMLTableElement;
}> = ({ editor, tableElement }) => {
  const [visibleRows, setVisibleRows] = React.useState<
    Array<{ row: HTMLTableRowElement; position: { left: number; top: number } }>
  >([]);

  const [activeMenu, setActiveMenu] = React.useState<{
    rowIndex: number;
    position: { left: number; top: number };
  } | null>(null);

  const [focusedRowIndex, setFocusedRowIndex] = React.useState<number | null>(null);

  const updateRowPositions = React.useCallback((): void => {
    const rect = tableElement.getBoundingClientRect();
    const rows = Array.from(tableElement.querySelectorAll("tr"));

    const rowPositions = rows.map((row) => {
      const rowRect = row.getBoundingClientRect();
      return {
        row,
        position: {
          left: rect.left - 10,
          top: rowRect.top + rowRect.height / 2,
        },
      };
    });

    setVisibleRows(rowPositions);
  }, [tableElement, activeMenu]);

  const updateFocusedRow = React.useCallback((): void => {
    if (!editor) return;

    const { from } = editor.state.selection;

    let tableRow: HTMLTableRowElement | null = null;
    let rowIndex = -1;

    const domPos = editor.view.domAtPos(from);
    if (domPos.node && domPos.node.nodeType === Node.ELEMENT_NODE) {
      const element = domPos.node as Element;
      const rowElement = element.closest("tr");
      if (rowElement && tableElement.contains(rowElement)) {
        tableRow = rowElement;
        const allRows = Array.from(tableElement.querySelectorAll("tr"));
        rowIndex = allRows.indexOf(tableRow);
      }
    }

    const newFocusedRowIndex = rowIndex >= 0 ? rowIndex : null;

    if (focusedRowIndex !== newFocusedRowIndex && activeMenu) {
      setActiveMenu(null);
    }

    setFocusedRowIndex(newFocusedRowIndex);
  }, [editor, tableElement, focusedRowIndex, activeMenu, visibleRows]);

  React.useEffect(() => {
    updateRowPositions();

    const handleScroll = (): void => updateRowPositions();
    const handleResize = (): void => updateRowPositions();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [updateRowPositions]);

  React.useEffect(() => {
    if (!editor) return;

    const handleUpdate = (): void => {
      setTimeout(() => {
        updateRowPositions();
        updateFocusedRow();
      }, 50);
    };

    const handleSelectionUpdate = (): void => {
      updateFocusedRow();
    };

    editor.on("update", handleUpdate);
    editor.on("selectionUpdate", handleSelectionUpdate);

    return (): void => {
      editor.off("update", handleUpdate);
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, [editor, updateRowPositions, updateFocusedRow]);

  const handleRowActionClick = (
    rowIndex: number,
    position: { left: number; top: number },
  ): void => {
    setActiveMenu({ rowIndex, position });
  };

  const closeMenu = (): void => {
    setActiveMenu(null);
  };

  const handleInsertRowAbove = (): void => {
    if (!editor) return;

    editor.chain().focus().addRowBefore().run();

    closeMenu();
  };

  const handleInsertRowBelow = (): void => {
    if (!editor) return;

    editor.chain().focus().addRowAfter().run();

    closeMenu();
  };

  const handleDeleteRow = (): void => {
    if (!editor) return;

    // Check if this is the last row in the table
    const rows = Array.from(tableElement.querySelectorAll("tr"));
    const isLastRow = rows.length === 1;

    if (isLastRow) {
      // If it's the last row, delete the entire table
      editor.chain().focus().deleteTable().run();
    } else {
      // Normal row deletion
      editor.chain().focus().deleteRow().run();
    }

    closeMenu();
  };

  return (
    <>
      {focusedRowIndex !== null && visibleRows[focusedRowIndex] && (
        <RowActionButton
          type="button"
          title="Row actions"
          key={`row-action-${focusedRowIndex}`}
          onClick={() =>
            handleRowActionClick(focusedRowIndex, visibleRows[focusedRowIndex].position)
          }
          style={{
            left: `${visibleRows[focusedRowIndex].position.left}px`,
            top: `${visibleRows[focusedRowIndex].position.top}px`,
            transform: "translateY(-50%)",
          }}
        >
          <DragIndicatorIcon htmlColor={colors.common.white} sx={{ fontSize: 16 }} />
        </RowActionButton>
      )}

      {activeMenu && (
        <>
          <MenuBackdrop
            onClick={closeMenu}
            role="button"
            tabIndex={-1}
            aria-label="Close menu"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                closeMenu();
              }
            }}
          />

          <ActionMenu
            style={{
              left: `${activeMenu.position.left + 25}px`,
              top: `${activeMenu.position.top}px`,
              transform: "translateY(-50%)",
            }}
          >
            <MenuItem type="button" onClick={() => handleInsertRowAbove()}>
              <NorthIcon htmlColor={colors.grey[400]} />
              Insert row above
            </MenuItem>

            <MenuItem type="button" onClick={() => handleInsertRowBelow()}>
              <SouthIcon htmlColor={colors.grey[400]} />
              Insert row below
            </MenuItem>

            <Divider />

            <MenuItem
              type="button"
              onClick={() => handleDeleteRow()}
              style={{ color: colors.error.dark }}
            >
              <DeleteOutlineIcon htmlColor={colors.error.dark} />
              Delete row
            </MenuItem>
          </ActionMenu>
        </>
      )}
    </>
  );
};
